import { ExceptionLevel, ExceptionType } from '../entity-details/Exception.model';

export const ActivityType = {
    // A person asked for a request, as opposed to the system producing one on its own. Always accompanied
    // by the REQUEST_CREATED entries describing what was then created, which stay system-attributed.
    NEW_LINK_CREATED: 'NEW_LINK_CREATED',
    REQUEST_CREATED: 'REQUEST_CREATED',
    RESPONSE_RECEIVED: 'RESPONSE_RECEIVED',
    REQUEST_TIMEOUT: 'REQUEST_TIMEOUT',
    REQUEST_TERMINATED: 'REQUEST_TERMINATED',
    EVALUATION_COMPLETED: 'EVALUATION_COMPLETED',
    // A client sent a partly answered verification for review without waiting for the rest.
    FORCED_SUBMISSION: 'FORCED_SUBMISSION',
    NOTE_ADDED: 'NOTE_ADDED',
    DOCUMENT_UPLOADED: 'DOCUMENT_UPLOADED',
    EMAIL_SENT: 'EMAIL_SENT',
    EMAIL_ERROR: 'EMAIL_ERROR',
    EVAL_OVERRIDE_GRANTED: 'EVAL_OVERRIDE_GRANTED',
    EVAL_OVERRIDE_REMOVED: 'EVAL_OVERRIDE_REMOVED',
    INSURED_PAUSED: 'INSURED_PAUSED',
    INSURED_UNPAUSED: 'INSURED_UNPAUSED',
    ENTITY_PROPERTY_CHANGED: 'ENTITY_PROPERTY_CHANGED',
    COMPLIANCE_STATUS_CHANGED: 'COMPLIANCE_STATUS_CHANGED',
    MEMBER_OUTREACH: 'MEMBER_OUTREACH',
    PRODUCER_OUTREACH: 'PRODUCER_OUTREACH',
    ENTITY_DEACTIVATED: 'ENTITY_DEACTIVATED',
    ENTITY_ACTIVATED: 'ENTITY_ACTIVATED',
    RISK_PROFILE_ADDED: 'RISK_PROFILE_ADDED',
    RISK_PROFILE_REMOVED: 'RISK_PROFILE_REMOVED',
    ENTITY_ACTION_RESOLVED: 'ENTITY_ACTION_RESOLVED',
    ENTITY_ACTION_REVERTED: 'ENTITY_ACTION_REVERTED',
    DOCUMENT_DATA_REVIEW_COMPLETED: 'DOCUMENT_DATA_REVIEW_COMPLETED',

    // Global (non-entity) events. These describe platform configuration changes for the relying party and
    // are served by the RP-level activity-history endpoint; they never appear on an entity's History tab.
    RISK_PROFILE_CREATED: 'RISK_PROFILE_CREATED',
    RISK_PROFILE_UPDATED: 'RISK_PROFILE_UPDATED',
    RISK_PROFILE_DELETED: 'RISK_PROFILE_DELETED',
    RISK_PROFILE_CRITERIA_CHANGED: 'RISK_PROFILE_CRITERIA_CHANGED',
    NOTIFICATION_ENABLEMENT_CHANGED: 'NOTIFICATION_ENABLEMENT_CHANGED',
    NOTIFICATION_CONTENT_CHANGED: 'NOTIFICATION_CONTENT_CHANGED',
    BRANDING_CHANGED: 'BRANDING_CHANGED',
    RP_PROFILE_CHANGED: 'RP_PROFILE_CHANGED',
    CADENCE_CHANGED: 'CADENCE_CHANGED',
    WEBHOOK_CREATED: 'WEBHOOK_CREATED',
    WEBHOOK_UPDATED: 'WEBHOOK_UPDATED',
    WEBHOOK_DELETED: 'WEBHOOK_DELETED',
    SSO_SETTINGS_CHANGED: 'SSO_SETTINGS_CHANGED',
} as const;

export type ActivityType = (typeof ActivityType)[keyof typeof ActivityType];

export const GLOBAL_ACTIVITY_TYPES: ActivityType[] = [
    ActivityType.RISK_PROFILE_CREATED,
    ActivityType.RISK_PROFILE_UPDATED,
    ActivityType.RISK_PROFILE_DELETED,
    ActivityType.RISK_PROFILE_CRITERIA_CHANGED,
    ActivityType.NOTIFICATION_ENABLEMENT_CHANGED,
    ActivityType.NOTIFICATION_CONTENT_CHANGED,
    ActivityType.BRANDING_CHANGED,
    ActivityType.RP_PROFILE_CHANGED,
    ActivityType.CADENCE_CHANGED,
    ActivityType.WEBHOOK_CREATED,
    ActivityType.WEBHOOK_UPDATED,
    ActivityType.WEBHOOK_DELETED,
    ActivityType.SSO_SETTINGS_CHANGED,
];

// Everything a global event is not. Derived rather than hand-listed so a new entity event type is picked up
// by the entity filter without a second edit here; a new global type must be added to the list above.
export const ENTITY_ACTIVITY_TYPES: ActivityType[] =
    Object.values(ActivityType).filter((type) => !GLOBAL_ACTIVITY_TYPES.includes(type));

export const ResourceType = {
    VERIFICATION: 'VERIFICATION',
    NOTE: 'NOTE',
    DOCUMENT: 'DOCUMENT',
    EVAL_OVERRIDE: 'EVAL_OVERRIDE',
    INSURED: 'INSURED',
    RISK_PROFILE: 'RISK_PROFILE',
    ACTION: 'ACTION',
    DOCUMENT_DATA_REVIEW: 'DOCUMENT_DATA_REVIEW',
    RISK_PROFILE_CONFIG: 'RISK_PROFILE_CONFIG',
    NOTIFICATION_CONFIG: 'NOTIFICATION_CONFIG',
    PLATFORM_CONFIG: 'PLATFORM_CONFIG',
} as const;

export type ResourceType = (typeof ResourceType)[keyof typeof ResourceType];

export const VIEWABLE_RESOURCE_TYPES: ResourceType[] = [
    ResourceType.NOTE,
    ResourceType.DOCUMENT,
];

export const ActivityDeclineReason = {
    DECLINED: 'DECLINED',
    EXCEPTION_REQUESTED: 'EXCEPTION_REQUESTED',
    MISSING: 'MISSING',
    NOT_RENEWED: 'NOT_RENEWED',
} as const;

export type ActivityDeclineReason = (typeof ActivityDeclineReason)[keyof typeof ActivityDeclineReason];

export interface VerificationActivityDetails {
    type: string;
    verificationId: string;
    coverageType: string;
    instanceId?: string | null;
}

export interface EvaluationCompletedDetails {
    type: string;
    verificationId: string;
    coverageType: string;
    instanceId?: string | null;
    declineReason?: ActivityDeclineReason;
    hasExtractedData?: boolean;
}

export interface ForcedSubmissionDetails {
    type: string;
    verificationId: string;
}

export const NotificationEventType = {
    SENT: 'SENT',
    REJECTED: 'REJECTED',
    BOUNCE: 'BOUNCE',
    SPAM: 'SPAM',
} as const;

export type NotificationEventType = (typeof NotificationEventType)[keyof typeof NotificationEventType];

export interface NotificationDetails {
    type: string;
    eventType: NotificationEventType;
    externalNotificationId?: string | null;
    sendId?: string | null;
    recipientEmail: string;
    cc?: string[] | null;
    fromEmail?: string | null;
    fromName?: string | null;
    notificationType: 'NEW' | 'NON_COMPLIANT' | 'EXPIRING' | 'NEW_ENTITY_BODY' | 'COMPLIANT';
    // legacy key for rows served by backends older than 2026.31 (pre-PRODUCT-26249 rename)
    verificationType?: 'NEW' | 'NON_COMPLIANT' | 'EXPIRING' | 'NEW_ENTITY_BODY' | 'COMPLIANT' | null;
    notificationOperation: 'INITIAL' | 'REMINDER';
    rejectReason?: string | null;
    subject?: string | null;
}

export interface EvalOverrideDetails {
    level?: ExceptionLevel | null;
    coverageType?: string | null; // present for REQUIREMENT_INSTANCE + COVERAGE_CRITERION levels
    criterionField?: string | null; // raw criterion field path (COVERAGE_CRITERION level); FE resolves the title
    expirationType?: ExceptionType | null; // granted events only: PERMANENT / UNTIL_DATE / UNTIL_COVERAGE_EXPIRATION
    expirationDate?: string | null; // ISO yyyy-mm-dd; populated only for granted UNTIL_DATE exceptions
    note: string | null; // user-provided or system-generated; null for pre-rollout exceptions
    scope?: string | null; // legacy pre-PRODUCT-26098 rows only (lower environments); no longer written
}

export interface EntityPropertyChangedDetails {
    changedPropertiesDisplayNames: string[]; // ordered
}

export interface ComplianceStatusChangedDetails {
    previousStatus: string; // raw backend complianceStatus enum value; may be a deprecated/unknown value
    newStatus: string;
}

export interface RiskProfileDetails {
    // The risk profile's display name as it stood when the event was recorded. Deliberately a snapshot:
    // the backend never updates it if the profile is later renamed, so historical rows stay accurate.
    // Shared by RISK_PROFILE_ADDED and RISK_PROFILE_REMOVED. The profile's id is the entry's resourceId.
    riskProfileName: string;
}

export const ActionResolution = {
    ACCEPTED: 'ACCEPTED',
    REJECTED: 'REJECTED',
} as const;

export type ActionResolution = (typeof ActionResolution)[keyof typeof ActionResolution];

export const NameMatchSubject = {
    ENTITY_NAME: 'ENTITY_NAME',
    ADDRESS: 'ADDRESS',
} as const;

export type NameMatchSubject = (typeof NameMatchSubject)[keyof typeof NameMatchSubject];

export interface EntityActionResolvedDetails {
    // The action's `$action` discriminator token, the same value the Actions tab receives (see
    // ActionType.model.ts) - e.g. 'EntityNameMatchingV1'. Deliberately the raw token rather than a
    // display string: the backend never composes copy for this event. Typed as a plain string because
    // the backend may add action types before this app knows them; render via
    // formatEntityActionResolvedSummary, which degrades gracefully on an unrecognised value.
    actionType: string;
    resolution: ActionResolution;
    // Only present for entity name matching, whose one action type covers two different checks - the
    // entity's own name and an address. Absent for every other action type, and for rows the backend
    // wrote before it began sending this. Derived by the backend, which owns the set of criterion
    // fields that count as addresses.
    nameMatchSubject?: NameMatchSubject;
}

export interface EntityActionRevertedDetails {
    // The same `$action` discriminator token EntityActionResolvedDetails carries, so both events read
    // from one label map.
    actionType: string;
    // Free text the reviewer typed when withdrawing the decision, and optional because the field is.
    // Customer-supplied, unlike the rest of this payload, so anything rendering it must escape it.
    revertReason: string | null;
}

export interface DocumentDataReviewDetails {
    // The requirements this review covers, as raw backend coverage-type values. A list because all
    // insurance-category requirements arrive bundled into a single review, while each non-insurance
    // requirement gets its own. The review's id is
    // the entry's resourceId.
    requirements: { requirement: string, instance?: string | null }[];
}

export interface ManagedServicesActivityDetails {
    description: string; // free-form text provided by the managed-services team; shared by both outreach event types
}

export interface RiskProfileConfigDetails {
    // The risk profile's display name as it stood when the event was recorded. A snapshot: the backend never
    // updates it if the profile is later renamed. The profile's id is the entry's resourceId.
    riskProfileName: string;
    // Display names of the fields a RISK_PROFILE_UPDATED changed. Empty for created, deleted and
    // criteria-changed events.
    changedFields?: string[];
}

export interface NotificationConfigDetails {
    notificationType: 'NEW' | 'NON_COMPLIANT' | 'EXPIRING' | 'NEW_ENTITY_BODY' | 'COMPLIANT';
    // The new value, present on NOTIFICATION_ENABLEMENT_CHANGED for each flag that changed; a save that
    // flips both initEnabled and remindersEnabled populates both. Absent on NOTIFICATION_CONTENT_CHANGED,
    // which records that templates changed but not their text.
    initEnabled?: boolean | null;
    remindersEnabled?: boolean | null;
}

export interface PlatformConfigDetails {
    // Which configuration surface was touched: 'branding' | 'profile' | 'cadence' | 'webhook'. These events
    // come from endpoints this service proxies opaquely, so there is no before/after to show.
    target: string;
}

export type ActivityHistoryEntryDetails =
    | VerificationActivityDetails
    | EvaluationCompletedDetails
    | ForcedSubmissionDetails
    | NotificationDetails
    | EvalOverrideDetails
    | EntityPropertyChangedDetails
    | ComplianceStatusChangedDetails
    | RiskProfileDetails
    | EntityActionResolvedDetails
    | EntityActionRevertedDetails
    | DocumentDataReviewDetails
    | ManagedServicesActivityDetails
    | RiskProfileConfigDetails
    | NotificationConfigDetails
    | PlatformConfigDetails;

export interface ActivityHistoryEntry {
    activityType: ActivityType;
    details: ActivityHistoryEntryDetails | null;
    resourceType: ResourceType;
    // Absent on global events, which have no entity resource to point at.
    resourceId: string | null;
    systemTags: string[];
    clientTags: string[];
    causedBy: string;
    createdAt: string;
}

export interface ActivityHistoryNavigation {
    next: number;
    prev: number;
    count: number;
}

export interface ActivityHistoryResult {
    navigation: ActivityHistoryNavigation;
    records: ActivityHistoryEntry[];
}

export interface ActivityHistoryActionableTags {
    clientTags: string[];
    systemTags: string[];
}

export interface ActivityHistoryQuery {
    skip?: number;
    limit?: number;
    entryType?: string[];
    tags?: string[];
    causedBy?: string;
}
